import { useEffect, useState } from 'react';
import AgentAvatar from '../common/AgentAvatar';
import styles from './LeadDifference.module.css';

const GATES = [
  { title: 'The email verifies', body: 'Deliverability-checked in real time, so you never torch your sender reputation on a dead address.' },
  { title: 'The person still works there', body: 'Employment verification confirms they haven’t moved on since the database was last refreshed.' },
  { title: 'The company fits your ICP', body: 'Vetted against your seller profile (titles, industry, size, and territory) before it reaches your list.' },
];

// The gauntlet: seven sample leads run the three gates live. failAt is the gate
// index that catches them (-1 = survives all three); reasons mirror what the real
// Verifier reports. Rows stay mounted throughout so the panel never resizes.
const LEADS = [
  { name: 'Sarah Chen',     role: 'VP Ops · Meridian Freight',          failAt: -1 },
  { name: 'Mike Torres',    role: 'Dir. Logistics · Gulf Haulage',      failAt: 0, reason: 'email bounced' },
  { name: 'Dana Whitfield', role: 'VP Ops · Crestline 3PL',             failAt: -1 },
  { name: 'Rob Alvarez',    role: 'VP Ops · TalentBridge Staffing',     failAt: 2, reason: 'staffing firm, off-ICP' },
  { name: 'Priya Nair',     role: 'Head of Ops · Southway Carriers',    failAt: -1 },
  { name: 'James Okafor',   role: 'VP Ops · Atlas Trucking',            failAt: 1, reason: 'left company 4 mo ago' },
  { name: 'Elena Vasquez',  role: 'Ops Director · Bluegrass Logistics', failAt: -1 },
];

const initialRows = () => LEADS.map(() => ({ checks: [null, null, null], verdict: null }));

const finalRows = () => LEADS.map((l) => ({
  checks: [0, 1, 2].map((g) => {
    if (l.failAt === -1 || g < l.failAt) return 'pass';
    if (g === l.failAt) return 'fail';
    return null; // never reached
  }),
  verdict: l.failAt === -1 ? 'in' : 'out',
}));

export default function LeadDifference() {
  const [rows, setRows] = useState(initialRows);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    // Reduced motion: show the finished gauntlet, skip the theater.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRows(finalRows());
      return undefined;
    }

    let alive = true;
    const timers = [];
    const wait = (ms) => new Promise((res) => timers.push(setTimeout(res, ms)));
    const setCheck = (r, g, val) => {
      if (!alive) return;
      setRows((prev) => prev.map((row, i) => (
        i === r ? { ...row, checks: row.checks.map((c, j) => (j === g ? val : c)) } : row
      )));
    };
    const setVerdict = (r, val) => {
      if (!alive) return;
      setRows((prev) => prev.map((row, i) => (i === r ? { ...row, verdict: val } : row)));
    };

    (async () => {
      while (alive) {
        if (alive) { setRows(initialRows()); setActive(-1); }
        await wait(900);
        for (let r = 0; r < LEADS.length; r += 1) {
          if (alive) setActive(r);
          const lead = LEADS[r];
          const lastGate = lead.failAt === -1 ? 2 : lead.failAt;
          for (let g = 0; g <= lastGate; g += 1) {
            await wait(260);
            setCheck(r, g, g === lead.failAt ? 'fail' : 'pass');
          }
          await wait(220);
          setVerdict(r, lead.failAt === -1 ? 'in' : 'out');
          await wait(180);
        }
        if (alive) setActive(-1);
        await wait(6000);
      }
    })();

    return () => { alive = false; timers.forEach(clearTimeout); };
  }, []);

  const countedIn  = rows.filter((r) => r.verdict === 'in').length;
  const countedOut = rows.filter((r) => r.verdict === 'out').length;

  return (
    <section id="difference" className={styles.section}>
      <div className={styles.inner}>
        <span className={styles.label}>What makes a RepBuddy lead different</span>
        <h2 className={styles.heading}>A lead only counts when it's real.</h2>
        <p className={styles.lead}>
          Other tools sell raw credits and let you discover the bounces yourself. RepBuddy
          only counts a lead when all three checks pass.
        </p>

        <div className={styles.gates}>
          {GATES.map((g) => (
            <div key={g.title} className={styles.gate}>
              <span className={styles.check} aria-hidden="true">✓</span>
              <div>
                <h3 className={styles.gateTitle}>{g.title}</h3>
                <p className={styles.gateBody}>{g.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Watch the gates work: sample leads filtered live */}
        <div className={styles.gauntlet} aria-hidden="true">
          <div className={styles.gauntletHead}>
            <span className={styles.gTitle}>
              <AgentAvatar agent="verifier" size={22} /> watch the gates work
            </span>
            <span className={styles.gLegend}>checks: email → employed → icp</span>
          </div>

          {LEADS.map((l, i) => {
            const row = rows[i];
            const rowCls = [
              styles.row,
              active === i ? styles.rowActive : '',
              row.verdict === 'in' ? styles.rowIn : '',
              row.verdict === 'out' ? styles.rowOut : '',
            ].join(' ');
            return (
              <div key={l.name} className={rowCls}>
                <div className={styles.who}>
                  <span className={styles.name}>{l.name}</span>
                  <span className={styles.role}>{l.role}</span>
                </div>
                <div className={styles.dots}>
                  {row.checks.map((c, g) => (
                    <span
                      key={g}
                      className={`${styles.dot} ${c === 'pass' ? styles.dotPass : ''} ${c === 'fail' ? styles.dotFail : ''}`}
                    >
                      {c === 'pass' ? '✓' : c === 'fail' ? '✕' : ''}
                    </span>
                  ))}
                </div>
                <span className={`${styles.rowTag} ${row.verdict === 'in' ? styles.tagIn : ''} ${row.verdict === 'out' ? styles.tagOut : ''}`}>
                  {row.verdict === 'in' ? 'Verified' : row.verdict === 'out' ? l.reason : ''}
                </span>
              </div>
            );
          })}

          <div className={styles.tally}>
            <span className={styles.tallyIn}>{countedIn} verified</span>
            <span className={styles.tallyOut}>{countedOut} filtered</span>
            <span className={styles.tallyNote}>other tools would charge you for all {LEADS.length}</span>
          </div>
        </div>

        <div className={styles.filter}>
          <span className={styles.filterTag}>Auto-filtered</span>
          <p className={styles.filterText}>
            Junk companies (staffing firms, agencies, and VCs posing as operators) get
            removed automatically. You never pay for a lead you couldn't sell to.
          </p>
        </div>
      </div>
    </section>
  );
}
